// src/padplanner/PlansListPage.jsx
//
// /padplanner/plans, mockup 2: a 370px panel of plan cards beside a map of
// plan outlines.
//
// ── THE PANEL ──────────────────────────────────────────────────────────────
//
//   head    "PadPlanner", the plan count, Dashboard and Template library links;
//           search and New plan (an editor only); the tabs All · Drafts · In review ·
//           Approved · Archived (decision 4), each with its count
//   body    two tiles, Drafts and Starts in the next 30 days, real counts
//           only; type chips, an "Owned by me" chip and a landowner filter
//           (158: any that requires one, none, or one landowner by name, from
//           the org's relationships, revoked ones too); the plan cards; Show
//           more (keyset paging, REM-603's cursor)
//
// Changes requested and Issued have no tab in 370px. ?status= still takes
// them (the dashboard legend links there), and the panel then says which
// status it shows, with a way back to All.
//
// ── SELECTING A PLAN ───────────────────────────────────────────────────────
//
// A card or an outline selects; it does not navigate. The outline is drawn
// thicker and the map fits to it, the floating card shows the plan (getPlan),
// and the time strip lists that plan's imagery: a chip shows that capture
// under the outlines, the pinned one is marked. "Open plan" opens it.
//
// ── CUT ────────────────────────────────────────────────────────────────────
//
//   S6  Waiting on me, With others, the cards' progress bars, the floating
//       card's approval chain and Open review, the legend's review relations
//   S5  the Warnings tile and the floating card's layout checks
//   --  "Follow mode" (no per-plan imagery follow exists); "Open in editor"
//       is "Open plan" (the editor is S4)
//
// The project is the TopNav picker's (x-aiprime-project, 148): the counts,
// the cards and the outlines are asked for again when it changes, and
// ProjectScope says which project is shown and what it leaves out.
//
// A failed read is said with a Retry (ReadFailed): the counts, the cards and
// the map's outlines each their own; never zero plans or an empty map.
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Plus, X } from "lucide-react";
import PadPlannerGate from "./PadPlannerGate";
import PadPlannerMap from "./PadPlannerMap";
import SidePanel, { SectionLabel } from "./SidePanel";
import StatusPill from "./StatusPill";
import { ImageryStrip } from "../components/imagery/TimeStrip";
import { selectAndZoom, sitePlannerRows } from "./imageryStripRows";
import { getDashboard, getEpochs, getLandownerRelationships, getPerimeters, getPlan, listPlans } from "../api/padplannerApi";
import { useProjectName } from "./useProjectName";
import ProjectScope from "./ProjectScope";
import ReadFailed from "./ReadFailed";
import { filtersFor, countForTab } from "./planFilters";
import { WORLD_BBOX, boundsOf } from "./planMapData";
import { STATUS_STYLE } from "./planStatus";
import {
  PLAN_STATUSES, STATUS_LABELS, PLAN_TYPES, PLAN_TYPE_LABELS, formatAcres, formatDate, imageryEmptyText, imageryLabel,
  openingCapture, includeForCapture, referenceForCapture, creditForCapture,
} from "./planVocabulary";
import { PADPLANNER_DISPLAY_NAME } from "../constants/analyticsProducts";

export default function PlansListPage() {
  return (
    <PadPlannerGate section="plans">
      <PlansList />
    </PadPlannerGate>
  );
}

const LIST_TABS = Object.freeze([
  { key: "all", label: "All" }, { key: "draft", label: "Drafts" }, { key: "in_review", label: "In review" },
  { key: "approved", label: "Approved" }, { key: "archived", label: "Archived" },
]);
const PAGE = 50;

function PlansList() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const tab = PLAN_STATUSES.includes(search?.status) || search?.status === "archived" ? search.status : "all";
  const { projectKey, projectName, showAllProjects } = useProjectName();
  const [planType, setPlanType] = useState("");
  const [mine, setMine] = useState(false);
  const [landowner, setLandowner] = useState("");
  // The names the landowner filter offers. Without them it still offers any and none.
  const [owners, setOwners] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getLandownerRelationships()
      .then((r) => { if (!cancelled) setOwners((r.relationships ?? []).map((x) => ({ id: x.owner_org_id, name: x.owner_org_name }))); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const [q, setQ] = useState("");
  const [qApplied, setQApplied] = useState("");
  const [page, setPage] = useState({ rows: [], cursor: null, error: null, loading: true });
  const [selectedId, setSelectedId] = useState(null);

  // Each read is kept per project and attempt: a retry asks again, and a
  // failure never leaves another project's answer showing.
  const [attempts, setAttempts] = useState({ dash: 0, list: 0, outlines: 0 });
  const again = (k) => setAttempts((a) => ({ ...a, [k]: a[k] + 1 }));
  const dashKey = `${projectKey}:${attempts.dash}`;
  const [dashState, setDashState] = useState({ key: null, dash: null, error: null });
  useEffect(() => {
    let cancelled = false;
    getDashboard()
      .then((d) => { if (!cancelled) setDashState({ key: dashKey, dash: d, error: null }); })
      .catch((e) => { if (!cancelled) setDashState({ key: dashKey, dash: null, error: e }); });
    return () => { cancelled = true; };
  }, [dashKey]);
  const dash = dashState.key === dashKey ? dashState.dash : null;
  const dashError = dashState.key === dashKey ? dashState.error : null;

  const filters = filtersFor({ tab, planType, owner: mine ? "me" : "", landowner, q: qApplied });
  const filterKey = JSON.stringify(filters);
  useEffect(() => {
    let cancelled = false;
    listPlans({ ...JSON.parse(filterKey), limit: PAGE })
      .then((r) => { if (!cancelled) setPage({ rows: r.plans, cursor: r.next_cursor, error: null, loading: false }); })
      .catch((e) => { if (!cancelled) setPage({ rows: [], cursor: null, error: e.message, loading: false }); });
    return () => { cancelled = true; };
  }, [filterKey, projectKey, attempts.list]);

  function more() {
    setPage((p) => ({ ...p, loading: true }));
    listPlans({ ...filters, limit: PAGE, cursor: page.cursor })
      .then((r) => setPage((p) => ({ rows: [...p.rows, ...r.plans], cursor: r.next_cursor, error: null, loading: false })))
      .catch((e) => setPage((p) => ({ ...p, error: e.message, loading: false })));
  }

  // Every outline at once (the dashboard's world box, capped at 500), not
  // the viewport's: the map opens fitted to the listed plans, which needs
  // their outlines before the view is anywhere near them. Asked again when
  // the project changes.
  const outlinesKey = `${projectKey}:${attempts.outlines}`;
  const [outlinesState, setOutlinesState] = useState({ key: null, fc: null, error: null });
  useEffect(() => {
    let cancelled = false;
    getPerimeters(WORLD_BBOX)
      .then((fc) => { if (!cancelled) setOutlinesState({ key: outlinesKey, fc, error: null }); })
      .catch((e) => { if (!cancelled) setOutlinesState({ key: outlinesKey, fc: null, error: e }); });
    return () => { cancelled = true; };
  }, [outlinesKey]);
  const outlines = outlinesState.key === outlinesKey ? outlinesState.fc : null;
  const outlinesError = outlinesState.key === outlinesKey ? outlinesState.error : null;
  const initialBounds = useMemo(() => listedBounds(outlines, page.loading ? null : page.rows), [outlines, page.loading, page.rows]);

  const goTab = (key) => navigate({ to: "/padplanner/plans", search: key === "all" ? {} : { status: key } });
  const openPlan = (id) => navigate({ to: "/padplanner/plans/$planId", params: { planId: id } });
  const offTab = !LIST_TABS.some((t) => t.key === tab);
  const counts = dash?.counts ?? null;

  const head = (
    <>
      <div className="flex items-baseline justify-between gap-3">
        <SectionLabel>{PADPLANNER_DISPLAY_NAME}</SectionLabel>
        <span className="op-meta">
          {counts ? `${counts.active === 1 ? "1 plan" : `${counts.active} plans`} · ` : ""}
          <button type="button" className="hover:text-fg-primary" onClick={() => navigate({ to: "/padplanner" })}>Dashboard</button>
          {" · "}
          <button type="button" className="hover:text-fg-primary" onClick={() => navigate({ to: "/padplanner/library" })}>Template library</button>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <form className="min-w-0 flex-1" onSubmit={(e) => { e.preventDefault(); setQApplied(q); }}>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search plans"
            placeholder="Plan number, title or API number"
            className="w-full rounded border border-bg-border bg-bg px-2 py-1 text-[12.5px] text-fg-primary placeholder:text-fg-secondary" />
        </form>
        {dash?.can_edit && (
          <button type="button" className="op-btn op-btn--primary op-btn--sm inline-flex shrink-0 items-center gap-1"
            onClick={() => navigate({ to: "/padplanner/new" })}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" /> New plan
          </button>
        )}
      </div>
      <div className="-mb-3 flex gap-0.5 border-b border-bg-border" role="tablist" aria-label="Plan status">
        {LIST_TABS.map((t) => {
          const n = countForTab(counts, t.key);
          return (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => goTab(t.key)}
              className={`-mb-px whitespace-nowrap border-b-2 px-2 py-1.5 text-[12px] ${tab === t.key ? "border-accent-action text-fg-primary" : "border-transparent text-fg-secondary hover:text-fg-primary"}`}>
              {t.label}{n !== null ? <span className="ml-1 op-mono text-fg-secondary">{n}</span> : null}
            </button>
          );
        })}
      </div>
    </>
  );

  return (
    // h-full: the shell's <main> is a block box, where flex-1 does nothing and
    // the row stopped at its content's height. The panel and map now fill it.
    <div className="flex h-full min-h-0">
      <SidePanel label="Plans" head={head}>
        {offTab && (
          <div className="flex items-center justify-between gap-2 rounded border border-bg-border px-2 py-1.5 text-[12.5px]" data-testid="pp-status-filter">
            <span>Showing <span className="text-fg-primary">{STATUS_LABELS[tab]}</span> plans</span>
            <button type="button" className="inline-flex items-center gap-1 text-fg-secondary hover:text-fg-primary" onClick={() => goTab("all")}>
              <X className="h-3 w-3" aria-hidden="true" /> All
            </button>
          </div>
        )}

        <ProjectScope projectName={projectName} outside={counts?.outside_project ?? null} onShowAll={showAllProjects} />
        {dashError && <ReadFailed what="the plan counts" error={dashError} onRetry={() => again("dash")} testid="pp-counts-failed" />}
        {counts && (
          <div className="grid grid-cols-2 gap-2" data-testid="pp-tiles">
            <button type="button" className="flex flex-col items-start gap-1 rounded-md border border-bg-border px-3 py-2 text-left hover:border-fg-secondary"
              onClick={() => goTab("draft")}>
              <span className="op-label uppercase">Drafts</span>
              <span className="op-figure text-[22px] leading-none text-fg-primary" data-testid="pp-tile-drafts">{counts.by_status.draft}</span>
            </button>
            <div className="flex flex-col items-start gap-1 rounded-md border border-bg-border px-3 py-2">
              <span className="op-label uppercase">Starts in next 30 days</span>
              <span className="op-figure text-[22px] leading-none text-fg-primary" data-testid="pp-tile-due">{counts.due_30d}</span>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filters">
          {PLAN_TYPES.map((t) => (
            <button key={t} type="button" aria-pressed={planType === t} onClick={() => setPlanType(planType === t ? "" : t)}
              className={`op-chip h-6 rounded-full px-2.5 py-0 text-[11.5px] ${planType === t ? "border-accent-action text-fg-primary" : "text-fg-secondary"}`}>
              {PLAN_TYPE_LABELS[t]}
            </button>
          ))}
          <button type="button" aria-pressed={mine} onClick={() => setMine(!mine)}
            className={`op-chip h-6 rounded-full px-2.5 py-0 text-[11.5px] ${mine ? "border-accent-action text-fg-primary" : "text-fg-secondary"}`}>
            Owned by me
          </button>
          <select value={landowner} onChange={(e) => setLandowner(e.target.value)} aria-label="Landowner" data-testid="pp-landowner-filter"
            className={`op-chip h-6 rounded-full bg-bg px-2 py-0 text-[11.5px] ${landowner ? "border-accent-action text-fg-primary" : "text-fg-secondary"}`}>
            <option value="">Any landowner</option>
            <option value="any">Requires landowner approval</option>
            <option value="none">No landowner approval</option>
            {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>

        {page.error && <ReadFailed what="plans" error={page.error} testid="pp-plans-failed"
          onRetry={page.rows.length ? more : () => { setPage((p) => ({ ...p, error: null, loading: true })); again("list"); }} />}
        <ul className="flex flex-col gap-2" aria-label="Plans">
          {page.rows.map((p) => (
            <li key={p.id}>
              <button type="button" data-testid="pp-plan-card" aria-pressed={selectedId === p.id} onClick={() => setSelectedId(p.id)}
                onDoubleClick={() => openPlan(p.id)}
                className={`flex w-full flex-col gap-1 rounded-md border px-3 py-2 text-left ${selectedId === p.id ? "border-accent-action bg-bg-softer" : "border-bg-border hover:border-fg-secondary"}`}>
                <span className="flex items-start justify-between gap-2">
                  <span className="min-w-0 text-[13px] text-fg-primary">{p.title}</span>
                  <StatusPill status={p.status} archived={Boolean(p.archived_at)} />
                </span>
                <span className="flex items-baseline justify-between gap-2">
                  <span className="op-meta min-w-0 truncate">
                    {PLAN_TYPE_LABELS[p.plan_type] ?? p.plan_type} · v{p.current_revision} · {p.owner?.display_name ?? (p.owner ? "Unknown user" : "No owner")}
                  </span>
                  <span className="op-mono shrink-0 text-[11.5px] text-fg-secondary">{formatDate(p.target_date) ?? ""}</span>
                </span>
                <span className="op-mono text-[11px] text-fg-secondary">{p.plan_number}</span>
              </button>
            </li>
          ))}
        </ul>
        {!page.loading && page.rows.length === 0 && !page.error && <p className="op-meta">No plans match.</p>}
        {page.cursor && (
          <button type="button" className="op-btn op-btn--sm self-start" disabled={page.loading} onClick={more}>Show more</button>
        )}
        {outlines?.truncated && <p className="op-meta">The map shows the 500 most recently updated plans.</p>}
      </SidePanel>

      <div className="relative min-w-0 flex-1">
        <SelectedPlanMap outlines={outlines} initialBounds={initialBounds} selectedId={selectedId}
          onSelect={setSelectedId} onClose={() => setSelectedId(null)} onOpen={openPlan}
          notice={outlinesError && (
            <ReadFailed what="the plans on the map" error={outlinesError} onRetry={() => again("outlines")} testid="pp-outlines-failed"
              className="rounded-md border border-bg-border bg-bg/95 px-3 py-1.5 shadow" />
          )} />
      </div>
    </div>
  );
}

/* ── the map, and what floats on it for the selected plan ──────────────── */

function SelectedPlanMap({ outlines, initialBounds, selectedId, onSelect, onClose, onOpen, notice = null }) {
  // Keyed by id, so a stale answer can't land on a newer selection. The time
  // strip is optional: a failed epochs read leaves the card, and says so with a Retry in the strip's place.
  const [detail, setDetail] = useState({ id: null, plan: null, lanes: null, error: null, lanesError: null });
  const [lanesAttempt, setLanesAttempt] = useState(0);
  // The chip shown, per plan: selecting another plan starts at its pin.
  const [cursor, setCursor] = useState({ id: null, key: null });

  useEffect(() => {
    if (!selectedId) return undefined;
    let cancelled = false;
    // GET /plans/:id answers {plan, can_edit}, as PlanPage reads it: the plan
    // is the answer's .plan, not the answer.
    Promise.allSettled([getPlan(selectedId), getEpochs(selectedId)]).then(([p, e]) => {
      if (cancelled) return;
      const loaded = p.status === "fulfilled" ? (p.value?.plan ?? null) : null;
      setDetail({
        id: selectedId,
        plan: loaded,
        error: p.status === "rejected" ? (p.reason?.message ?? "error") : loaded ? null : "the answer had no plan",
        lanes: e.status === "fulfilled" && loaded ? e.value : null,
        lanesError: e.status === "rejected" && loaded ? e.reason : null,
      });
    });
    return () => { cancelled = true; };
  }, [selectedId, lanesAttempt]);

  const current = selectedId && detail.id === selectedId ? detail : null;
  const plan = current?.plan ?? null;
  const pinned = plan?.revision?.imagery?.dataset_key ?? null;
  const cursorKey = cursor.id === selectedId ? cursor.key : null;
  // Opens on the pin, or with none the newest collect: shown, not pinned.
  const shownKey = current ? (cursorKey ?? openingCapture(pinned, current.lanes)) : null;

  return (
    <>
      <PadPlannerMap perimeters={outlines} autoPins initialBounds={initialBounds} onPlanClick={onSelect}
        selectedId={selectedId} focus={plan?.revision?.perimeter ?? null} focusKey={plan ? selectedId : null} rasterKey={shownKey}
        rasterCredit={creditForCapture({ pin: plan?.revision?.imagery, lanes: current?.lanes, key: shownKey })}
        footprint={{ role: "plansList", include: includeForCapture({ pin: plan?.revision?.imagery, lanes: current?.lanes, key: shownKey }),
          withReference: referenceForCapture({ pin: plan?.revision?.imagery, lanes: current?.lanes, key: shownKey }) }}
        bottom={current?.lanes ? ((strip) => (
          <ImageryStrip onChip={selectAndZoom((key) => setCursor({ id: selectedId, key }), strip)} rows={sitePlannerRows({ lanes: current.lanes,
            cursorKey: shownKey, markKey: pinned, emptyText: imageryEmptyText(current.lanes.imagery_entitled), ...strip })} />
        )) : current?.lanesError ? (
          <ReadFailed what="this plan's imagery dates" onRetry={() => setLanesAttempt((n) => n + 1)} testid="pp-lanes-failed"
            className="self-start rounded-md border border-bg-border bg-bg/95 px-3 py-1.5 shadow" />
        ) : null}
        hud={{
          // Mockup 2's floating card, under the zoom control.
          topLeft: selectedId && <PlanCard key="card" plan={plan} error={current?.error ?? null} onClose={onClose} onOpen={() => onOpen(selectedId)} />,
          topRight: notice,
          bottomLeft: <StatusLegend key="legend" />,
        }} />
    </>
  );
}

// Bottom left, per mockup 2: the map's bottomLeft slot (MapHud.jsx).
function StatusLegend() {
  return (
    <ul className="flex flex-col gap-0.5 rounded-md border border-bg-border bg-bg/85 px-3 py-2 shadow"
      aria-label="Status colors" data-testid="pp-map-legend">
      {PLAN_STATUSES.map((s) => (
        <li key={s} className={`flex items-center gap-1.5 text-[11.5px] ${STATUS_STYLE[s].text}`}>
          <span className="op-dot" aria-hidden="true" />
          <span className="text-fg-secondary">{STATUS_LABELS[s]}</span>
        </li>
      ))}
    </ul>
  );
}

function PlanCard({ plan, error, onClose, onOpen }) {
  // revision is always sent; its parts are guarded anyway, so one missing
  // field shows as absent rather than taking the page down.
  const rev = plan?.revision ?? {};
  const rows = plan ? [
    ["Area", formatAcres(rev.perimeter_area_m2) ?? "No perimeter"],
    ["Wells", String(Array.isArray(rev.wells) ? rev.wells.length : 0)],
    ["Target", formatDate(plan.target_date) ?? "—"],
    ["Owner", plan.owner?.display_name ?? (plan.owner ? "Unknown user" : "No owner")],
    ["Imagery", imageryLabel(rev.imagery) ?? "Not pinned"],
  ] : [];
  return (
    <section className="flex w-[300px] max-w-full flex-col gap-2 rounded-md border border-bg-border bg-bg/95 p-3 shadow-lg"
      aria-label="Selected plan" data-testid="pp-plan-floating">
      <div className="flex items-start justify-between gap-2">
        {plan ? (
          <div className="flex min-w-0 flex-col gap-1">
            <span className="op-mono text-[11px] text-fg-secondary">{plan.plan_number}</span>
            <span className="text-[14px] font-medium text-fg-primary">{plan.title}</span>
          </div>
        ) : <span className="op-meta">{error ? `Couldn't load the plan: ${error}` : "Loading…"}</span>}
        <button type="button" aria-label="Close" className="shrink-0 text-fg-secondary hover:text-fg-primary" onClick={onClose}>
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {plan && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill status={plan.status} archived={Boolean(plan.archived_at)} />
            <span className="op-meta">
              {PLAN_TYPE_LABELS[plan.plan_type] ?? plan.plan_type} · v{plan.current_revision}
            </span>
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[12.5px]">
            {rows.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="op-label uppercase">{k}</dt>
                <dd className="text-fg-primary">{v}</dd>
              </div>
            ))}
          </dl>
          <button type="button" className="op-btn op-btn--primary op-btn--sm self-end" onClick={onOpen}>Open plan</button>
        </>
      )}
    </section>
  );
}

/**
 * [w, s, e, n] round the listed plans' outlines, for the map's opening view;
 * every outline when no listed plan has one; null before both have loaded or
 * when there is nothing to fit.
 */
function listedBounds(outlines, rows) {
  if (!outlines || !rows) return null;
  const listed = new Set(rows.map((r) => r.id));
  const all = outlines.features ?? [];
  const mine = all.filter((f) => listed.has(f.properties?.id ?? f.id));
  const boxes = (mine.length ? mine : all).map((f) => boundsOf(f.geometry)).filter(Boolean);
  if (boxes.length === 0) return null;
  return [Math.min(...boxes.map((b) => b[0])), Math.min(...boxes.map((b) => b[1])),
    Math.max(...boxes.map((b) => b[2])), Math.max(...boxes.map((b) => b[3]))];
}
